// UI strings for the letters area, Finnish (default) and English side by side.
// Finnish is written in full and never truncated; en must have exactly the same shape.
import { defineArea } from '../area';

export const lettersFi = {
  title: 'Kirjeet',
  write: 'Kirjoita kirje',
  empty: 'Ei vielä kirjeitä. Aloita lyhyestä tervehdyksestä.',
  from: (name: string) => `Kirjoittaja: ${name}`,
  corrections: (n: number) => (n === 1 ? '1 korjaus' : `${n} korjausta`),
  unsent: 'Lähettämättä · lähtee, kun yhteys palaa',
  newTitle: 'Uusi kirje',
  editTitle: 'Muokkaa kirjettä',
  bodyLabel: 'Kirje valyriaksi',
  bodyPlaceholder: 'Rytsas, …',
  translationLabel: 'Oma käännös (valinnainen)',
  translationPlaceholder: 'Mitä halusit sanoa, suomeksi tai englanniksi',
  preview: 'Esikatselu',
  unknownWord: 'Sanaa ei löydy sanakirjasta, tai sen muotoa ei tunnisteta.',
  send: 'Lähetä kirje',
  saveEdit: 'Tallenna muutokset',
  bodyRequired: 'Kirjoita jotain ennen lähettämistä.',
  editWarning:
    'Kirjeessä on korjauksia. Ne siirtyvät tekstin mukana. Jos korjattu kohta muuttuu, korjaus näytetään aiempana korjauksena alkuperäisen tekstinsä kanssa.',
  notFound: 'Kirjettä ei löytynyt. Se on ehkä juuri lähetetty: avaa se listasta.',
  original: 'Korjauksineen',
  corrected: 'Korjattu versio',
  translation: 'Kirjoittajan käännös',
  correctionsH: 'Korjaukset',
  noCorrections: 'Ei korjauksia.',
  correctHint: 'Napauta korjattavaa sanaa tai maalaa kohta.',
  authorHint: 'Kumppanisi voi merkitä korjauksia tähän kirjeeseen.',
  waitSync: 'Korjauksia voi merkitä, kun kirje on lähtenyt.',
  selection: 'Valittu kohta',
  correctSel: 'Korjaa valinta',
  clearSel: 'Poista valinta',
  correctTitle: 'Korjaus',
  originalText: 'Alkuperäinen',
  replacement: 'Korjattu muoto',
  replacementHint: 'Jätä tyhjäksi, jos kohta pitää poistaa.',
  note: 'Selitys (valinnainen)',
  notePlaceholder: 'esim. objekti on akkusatiivissa',
  touchesDict: 'Tämä koskee sanakirjaa (lisää merkinnän tarkistusjonoon)',
  flagKind: 'Mistä on kyse?',
  saveCorrection: 'Tallenna korjaus',
  by: (name: string) => `korjasi ${name}`,
  flagged: 'merkitty tarkistusjonoon',
  earlier: 'Aiemmat korjaukset',
  earlierNote:
    'Kirjoittaja muutti näitä kohtia korjausten jälkeen, joten korjaukset eivät enää osu tekstiin. Ne näytetään sen tekstin kanssa, johon ne tehtiin.',
  notFitting: 'Korjaus ei enää osu tekstiin',
  redo: 'Valitse kohta uudelleen tekstistä ja korjaa se, jos se on yhä tarpeen.',
  unchanged: 'Muuta korjattua muotoa tai kirjoita selitys ennen tallennusta.',
  flagLetter: 'Koko kirje',
  skipped: (n: number): string =>
    n === 1
      ? '1 korjaus ei osu nykyiseen tekstiin tai menee päällekkäin toisen kanssa, joten sitä ei näytetä tekstissä.'
      : `${n} korjausta ei osu nykyiseen tekstiin tai menee päällekkäin toisten kanssa, joten niitä ei näytetä tekstissä.`,
  showNote: 'Näytä selitys',
  you: 'sinä',

  // ---- letter mode (translator phase 3 item 16, tone phase 4 item 17) ----
  draftOpen: 'Kirjoita ensin suomeksi tai englanniksi',
  draftIntro: 'Tarkista käännös ennen lähettämistä.',
  draftLang: 'Kirjoitat',
  draftLangs: { fi: 'suomeksi', en: 'englanniksi' } as Record<'fi' | 'en', string>,
  draftLabel: 'Kirje omalla kielelläsi',
  draftPlaceholder: (lang: 'fi' | 'en', to: string, from: string): string =>
    lang === 'fi' ? `Rakas ${to},\n\nNäin tänään lohikäärmeen.\n\nRakkaudella,\n${from}` : `Dear ${to},\n\nI saw a dragon today.\n\nLove,\n${from}`,
  draftTranslate: 'Käännä valyriaksi',
  draftUse: 'Siirrä kirjeeseen',
  draftUsed: 'Käännös siirrettiin kirjeeseen. Muokkaa sitä vapaasti ennen lähettämistä.',
  draftUnsure: (n: number): string => (n === 1 ? '1 rivi on epävarma: tarkista se.' : `${n} riviä on epävarmoja: tarkista ne.`),
  draftSure: 'Kaikki rivit käännettiin varmasti, mutta tarkista silti sanat ja muodot.',
  draftWait: 'Kääntäjä tarvitsee taivutusmuotojen hakemiston. Odota hetki.',

  // ---- layout v2 (docs/design/SIVUPALSTA.md "Kirjeet") ----
  /** The empty list's one action, and the margin's: the week's letter. */
  writeWeek: 'Kirjoita viikon kirje',
  reply: 'Kirjoita vastaus',
  /** The view switch's name (Korjauksineen / Korjattu versio). */
  view: 'Näytä',
  /** A partner's note on a line: its number and who wrote it. */
  noteBy: (n: number, name: string) => `Huomautus ${n} · ${name}`,
  /** The margin while writing: the line the caret is on. */
  lineH: (n: number) => `Rivi ${n}`,
  lineEmpty: 'Tyhjä rivi.',
  lineKnown: 'Sovellus tunnistaa rivin jokaisen sanan.',
  lineUnknown: (words: string) => `Näitä muotoja ei vielä tunnisteta: ${words}`,
  macronsH: 'Pitkät vokaalit',
  /** The label over a notice on a letters page (LettersWarn: a hairline, not a straw stripe). */
  warnLabel: 'Huomaa',
  // ---- phone fixes 2026-10-07: the read view's heading ----
  /** "Kirjeesi · 7.10." / "Axelin kirje · 7.10." (a name ending in a consonant takes -in: FINNISH-STYLE). */
  readTitle: (mine: boolean, name: string, date: string) => `${mine ? 'Kirjeesi' : `${name}${/[aeiouyäöå]$/i.test(name) ? 'n' : 'in'} kirje`} · ${date}`,
};

export const lettersEn: typeof lettersFi = {
  title: 'Letters',
  write: 'Write a letter',
  empty: 'No letters yet. Start with a short greeting.',
  from: (name) => `From: ${name}`,
  corrections: (n) => (n === 1 ? '1 correction' : `${n} corrections`),
  unsent: 'Not sent · sends when you are back online',
  newTitle: 'New letter',
  editTitle: 'Edit letter',
  bodyLabel: 'Letter in High Valyrian',
  bodyPlaceholder: 'Rytsas, …',
  translationLabel: 'Your own translation (optional)',
  translationPlaceholder: 'What you meant, in Finnish or English',
  preview: 'Preview',
  unknownWord: 'Not in the dictionary (or the form is not recognised).',
  send: 'Send letter',
  saveEdit: 'Save changes',
  bodyRequired: 'Write something before sending.',
  editWarning:
    'This letter has corrections. They move with the text. If a corrected place changes, the correction is shown as an earlier correction with the text it was made on.',
  notFound: 'Letter not found. It may have just been sent: open it from the list.',
  original: 'With corrections',
  corrected: 'Corrected version',
  translation: 'Writer’s translation',
  correctionsH: 'Corrections',
  noCorrections: 'No corrections.',
  correctHint: 'Tap the word to correct, or select the part.',
  authorHint: 'Your partner can mark corrections in this letter.',
  waitSync: 'The letter goes to the server first; corrections can be marked after that.',
  selection: 'Selected',
  correctSel: 'Correct selection',
  clearSel: 'Clear selection',
  correctTitle: 'Correction',
  originalText: 'Original',
  replacement: 'Corrected form',
  replacementHint: 'Leave empty if the part should be deleted.',
  note: 'Explanation (optional)',
  notePlaceholder: 'e.g. the object is in the accusative',
  touchesDict: 'This touches the dictionary (opens a flag in the review queue)',
  flagKind: 'What is it about?',
  saveCorrection: 'Save correction',
  by: (name) => `corrected by ${name}`,
  flagged: 'flagged for review',
  earlier: 'Earlier corrections',
  earlierNote:
    'The writer changed these places after they were corrected, so the corrections no longer fit the text. They are shown with the text they were made on.',
  notFitting: 'The correction no longer fits the text',
  redo: 'Select the place in the text again and correct it if it still needs it.',
  unchanged: 'Change the corrected form or write an explanation before saving.',
  flagLetter: 'The whole letter',
  skipped: (n) =>
    n === 1
      ? '1 correction does not fit the current text or overlaps another, so it is not shown in the text.'
      : `${n} corrections do not fit the current text or overlap others, so they are not shown in the text.`,
  showNote: 'Show explanation',
  you: 'you',

  draftOpen: 'Write it first in Finnish or English',
  draftIntro: 'Check the translation before sending.',
  draftLang: 'You write',
  draftLangs: { fi: 'in Finnish', en: 'in English' },
  draftLabel: 'The letter in your language',
  draftPlaceholder: (lang, to, from) =>
    lang === 'fi' ? `Rakas ${to},\n\nNäin tänään lohikäärmeen.\n\nRakkaudella,\n${from}` : `Dear ${to},\n\nI saw a dragon today.\n\nLove,\n${from}`,
  draftTranslate: 'Translate into Valyrian',
  draftUse: 'Move into the letter',
  draftUsed: 'The translation was moved into the letter. Edit it freely before sending.',
  draftUnsure: (n) => (n === 1 ? '1 line is unsure: check it.' : `${n} lines are unsure: check them.`),
  draftSure: 'Every line was translated with confidence, but still check the words and forms.',
  draftWait: 'The translator needs the index of inflected forms. Please wait a moment.',

  writeWeek: 'Write this week’s letter',
  reply: 'Write a reply',
  view: 'Show',
  noteBy: (n, name) => `Note ${n} · ${name}`,
  lineH: (n) => `Line ${n}`,
  lineEmpty: 'Empty line.',
  lineKnown: 'The app recognises every word on this line.',
  lineUnknown: (words) => `These forms are not recognised yet: ${words}`,
  macronsH: 'Long vowels',
  warnLabel: 'Note',
  readTitle: (mine, name, date) => `${mine ? 'Your letter' : `${name}’s letter`} · ${date}`,
};

// Messages.letters (i18n/area.ts: collected by area-registry.ts)
export const area = defineArea('letters', lettersFi, lettersEn);
declare module '../area' {
  interface AreaMessages extends Record<typeof area.key, typeof lettersFi> {}
}
